// The league's two elimination thresholds, in the units the UI prints. The
// arena API serves neither: they are the executor's drawdown limit and the
// backend's MIN_ACTIVE_STAKE default, both configured out of band.
export const DRAWDOWN_LIMIT_PCT = 20;
export const MIN_STAKE_SAN = 1000;

// The executor's own rule, served by no field: a soft-stopped account keeps
// its open signals for 14 days for the provider to close by hand, then the
// executor closes them itself. The backend only records the soft stop and
// closes the window once the account is flat.
const AUTO_CLOSE_MS = 14 * 24 * 60 * 60 * 1000;

export type Elimination = {
  reason: 'drawdown_limit_reached' | 'insufficient_stake' | 'other';
  // winding_down: soft-stopped, the positions still held keep running;
  // closed: the trading window has ended.
  phase: 'winding_down' | 'closed';
  // When the executor closes the remaining signals on its own (epoch ms):
  // the soft stop plus the 14-day grace. null without a parsable soft-stop
  // moment — a stop reported with no soft stop before it never had the grace.
  autoCloseAt: number | null;
};

// An elimination is a stop with a reason, winding down or closed. It stays
// the issuer's state for as long as this allocation is the issuer's current
// one — the caller resolves the live allocation, else the newest stopped
// one, per issuer, so a new allocation going live replaces it on its own and
// no time window applies. `legacy_backfill` marks history migrated with no
// recorded reason: a closed window, not an elimination. `other` is a reason
// the backend's list does not know yet; it renders with no reason clause.
export function eliminationOf(allocation: {
  status: 'active' | 'stopped' | 'soft_stopped' | 'pending';
  stop_reason:
    | 'drawdown_limit_reached'
    | 'insufficient_stake'
    | 'legacy_backfill'
    | 'other'
    | null;
  soft_stopped_at: string | null;
}): Elimination | null {
  if (allocation.status !== 'soft_stopped' && allocation.status !== 'stopped') {
    return null;
  }
  const reason = allocation.stop_reason;
  if (reason === null || reason === 'legacy_backfill') return null;
  const softStoppedAt = parseStamp(allocation.soft_stopped_at);
  return {
    reason,
    phase: allocation.status === 'soft_stopped' ? 'winding_down' : 'closed',
    autoCloseAt:
      Number.isNaN(softStoppedAt) ? null : softStoppedAt + AUTO_CLOSE_MS,
  };
}

// When the last punished deposit on a provider unfreezes (epoch ms): the
// latest `punishment_until` among the deposits that is still ahead of `now`
// (a deposit is punished exactly when it carries one). null while no
// punishment is running — none applied yet (an operator signs it by hand
// after the soft stop, so it can lag the elimination or never land) or every
// deadline has passed.
export function punishmentUnlockAt(
  stakes: readonly { punishment_until: string | null }[],
  now: number,
): number | null {
  const deadlines = stakes.flatMap((stake) => {
    const until = parseStamp(stake.punishment_until);
    return Number.isNaN(until) || until <= now ? [] : [until];
  });
  return deadlines.length === 0 ? null : Math.max(...deadlines);
}

// NaN for an absent or unparsable stamp — the one miss both readers test.
function parseStamp(stamp: string | null): number {
  // Stryker disable next-line ConditionalExpression: equivalent — Date.parse(null) is NaN as well
  return stamp === null ? NaN : Date.parse(stamp);
}
